import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import Hero from '../content/Hero';
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import AnyEvent from '../content/AnyEvent';
import HowItWorks from '../content/HowItWorks';
import CommunityFaq from '../faqs/CommunityFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

function CommunityEvents() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  const { content: cms } = usePageContent('misc_community_events');

  return (
    <div className='CateringPage'>
      <Meta
        title={cms?.meta?.title || `Food ${vehicle_type}s for Events & Festivals`}
        type='website'
        canonical='/food-truck-catering/community-events'
        image={cdnPhoto(`${citySlug}-community-event-food-truck-services`)}
        description={cms?.meta?.description || `Book food ${vehicleType}s for events and festivals in ${city_name}. Block parties, school events, fundraisers, markets, and festivals. Requesting quotes is free.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type}s for Events & Festivals in ${city_name}`,
            description: `Book food ${vehicleType}s for events and festivals in ${city_name}. Block parties, fundraisers, and large-scale festivals.`,
            url: '/food-truck-catering/community-events',
            serviceType: 'Food Truck Event Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Events & Festivals', item: '/food-truck-catering/community-events' }
            ]
          }
        }}
      />

      <Hero photo={`${CDN_BASE}/photos/${citySlug}-community-event-food-truck-services.webp`} isResponsive>
        <h1>{cms?.hero?.headline || `Food ${vehicleType}s for festivals and community events`}</h1>
        <CmsRichText
          html={cms?.hero?.subheadline}
          fallback={
            <p>Build the lineup you need for a market, school event, fundraiser, block party, or festival.</p>
          }
        />
        <Link to='/catering/requests/new' className='Button'>
          {cms?.hero?.cta_label || `Find ${vehicleType}s for your event`}
        </Link>
      </Hero>

      <HowItWorks className='Section' />

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-community-food-truck-festival.webp`} alt='Food trucks at a community festival' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Feed a crowd without the moving parts</h4>

            <p>Festivals, markets, and block parties need food that scales without a kitchen. {vehicle_type}s bring their own, serve fast, and pack up when it&#39;s over. Book one for a <Link to='/food-truck-catering/fundraiser'>school fundraiser</Link>, or line up several for a street fair so the crowd has a few cuisines to choose from.</p>

            <div>
              <Link to='/catering/requests/new' className='Button'>
                Get a free quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta + ' ' + styles.Cta__reversed}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-community-food-truck-fair.webp`} alt='Food truck at a community fair' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Sell-through or set menu, your call</h4>

            <p>For public events, {vehicleType}s can sell directly to the crowd. For a <Link to='/food-truck-catering/private-parties'>private party</Link> or a <Link to='/food-truck-catering/special-occasions'>company event</Link>, you cover a set menu and guests just order. Tell us which one you&#39;re planning, and we&#39;ll send the details to {vehicleType}s that do that kind of event.</p>

            <div>
              <Link to='/catering/requests/new' className='Button'>
                Get catering quotes
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FeaturedTrucks />

      <section className='Section'>
        <LogoList community />
      </section>

      <AnyEvent />

      <section className='Section--extraPadding'>
        <CommunityFaq />
      </section>

      <section className='Section'>
        <div className={styles.BookingCta}>
          <h3>Ready to find a {vehicleType}?</h3>
          <p>Requesting quotes is free. Compare options before you book.</p>
          <Link
            className='Button'
            to='/catering/requests/new'
          >
            Get catering quotes
          </Link>
        </div>
      </section>
    </div>
  );
}

export default CommunityEvents;
